<template>
  <div v-if="displayCardSelector">{{ tn("payWith") }}</div>
  <select v-if="displayCardSelector" id="select-credit-card" v-model="selectedCreditCard" class="select-css mb">
    <option v-for="c in creditCards" :key="c.id" :value="c">
      {{
        `${c.cardType} ${tn("endingIn")} ${removeStars(c.lastDigits)},
        ${tn("exp")} ${formatCCDateString(c.expirationDate)}`
      }}
    </option>
    <option value="+">
      {{ tn("addACard") }}
    </option>
  </select>
  <div v-if="props.showDetails">
    <ul
      v-if="typeof selectedCreditCard !== 'string' && selectedCreditCard && userStore.currentUser"
      id="current-credit-card-info"
      class="mb-double"
    >
      <li id="currentAccountHolder">{{ selectedCreditCard.accountHolder }}</li>
      <li id="currentAddress1">{{ selectedCreditCard.address.streetAddress1 }}</li>
      <li id="currentAddress2">{{ selectedCreditCard.address.streetAddress2 }}</li>
      <li id="currentCityStatePostalCode">
        {{
          `${selectedCreditCard.address.city}, ${selectedCreditCard.address.stateOrProvince} ${selectedCreditCard.address.postalCode}`
        }}
      </li>
      <li id="currentCountry">{{ selectedCreditCard.address.country }}</li>
    </ul>
    <!--Unauthenticated buyflow Payment Information-->
    <dl
      v-if="typeof selectedCreditCard !== 'string' && selectedCreditCard && !userStore.currentUser"
      id="current-credit-card-info"
      class="emphasize-dd"
    >
      <dt>{{ tn("cardName") }}</dt>
      <dd id="currentAccountHolder">{{ selectedCreditCard.accountHolder }}</dd>
      <dt>{{ tn("cardNumber") }}</dt>
      <dd id="currentCardLastDigits">**** **** **** {{ selectedCreditCard.lastDigits }}</dd>
      <dt>{{ tn("cardExpiration") }}</dt>
      <dd id="currentExpirationDate">{{ selectedCreditCard.expirationDate }}</dd>
      <dt>{{ tn("cardBillingAddress") }}</dt>
      <dd>
        <ul>
          <li id="currentAddress1">{{ selectedCreditCard.address.streetAddress1 }}</li>
          <li id="currentAddress2">{{ selectedCreditCard.address.streetAddress2 }}</li>
          <li id="currentCityStatePostalCode">
            {{
              `${selectedCreditCard.address.city}, ${selectedCreditCard.address.stateOrProvince} ${selectedCreditCard.address.postalCode}`
            }}
          </li>
          <li id="currentCountry">{{ selectedCreditCard.address.country }}</li>
        </ul>
      </dd>
    </dl>
    <!-- <p>
      <button type="button" class="btn-link left-link" @click="resetToAddCardPage()">
        {{ tn("changePayment") }}
        <svg-icon-component class="icon-sm" icon="edit" />
      </button>
    </p> -->
    <!-- <section v-if="showAutomaticRenewDisclaimer">
      <h3 class="mb">{{ tn("automaticRenewSubtitle") }}</h3>
      <p>{{ tn("automaticRenewP1", { expirationDate: props.renewDate }) }}</p>
      <p>{{ tn("automaticRenewP2") }}</p>
      <ul class="form mb">
        <li class="checkbox">
          <input id="checkbox-auto-renew-subscription" v-model="autoRenew" type="checkbox" />
          <label>{{ tn("acceptAutomaticRenew", { expirationDate: props.renewDate }) }}</label>
        </li>
      </ul>
    </section> -->
    <section v-if="props.showDetails" class="constrain-65 mt">
      <div class="mb-double">
        <button
          id="btnCheckOut"
          class="btn-primary"
          :disabled="typeof selectedCreditCard === 'string' || !selectedCreditCard"
          @click="checkOut()"
        >
          {{ t("Common.reviewOrder") }}
        </button>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { t } from "@/i18n";
import { ref, onBeforeMount, PropType, computed, watch } from "vue";
import { logEvent, logException } from "@/common/logger";
import { ICreditCard } from "@/common/api/unifiedPortal/interfaces";
import { formatCCDateString } from "@/common/dateFormat";
//import { autoRenewOptInCountries, autoRenewOptInUSStates } from "@/components/Buy/Payment/PaymentConfigs";
import { useBuyFlowStore } from "@/stores/buyFlow";
import { removeStars } from "@/components/Buy/BuyHelper";
//import { useNotificationsStore } from "@/stores/notifications";
//import SvgIconComponent from "@/components/shared/SvgIcon/SvgIcon.vue";
import { useUserStore } from "@/stores/user";

const componentName = "BuySelectPaymentComponent";

const props = defineProps({
  creditCardsProp: {
    type: Object as PropType<ICreditCard[]>,
    default: null,
    required: false,
  },
  renewDate: {
    type: String,
    default: "",
    required: false,
  },
  showDetails: {
    type: Boolean,
    default: false,
    required: false,
  },
});

const emits = defineEmits(["add-card", "selected-credit-card", "set-display-card-details-state"]);

logEvent("created", componentName);

const buyFlowStore = useBuyFlowStore();
const userStore = useUserStore();
const selectedCreditCard = ref<ICreditCard | string>();
//const autoRenew = ref<boolean>(false);
const creditCards = computed(() => {
  const ccs: ICreditCard[] = [];
  if (props.creditCardsProp !== null && props.creditCardsProp.length > 0) {
    const defaultCC = props.creditCardsProp.find(card => card.defaultPaymentMethod) ?? null;
    const otherCCs = props.creditCardsProp.filter(card => !card.defaultPaymentMethod) ?? null;
    if (defaultCC) ccs.push(defaultCC);
    for (const cc of otherCCs) {
      ccs.push(cc);
    }
  }
  return ccs;
});
// const notificationsStore = useNotificationsStore();

// const showAutomaticRenewDisclaimer = computed(() => {
//   let response = false;
//   if (typeof selectedCreditCard.value !== "string") {
//     response =
//       selectedCreditCard.value?.address?.country === autoRenewOptInCountries.us.toString() &&
//       autoRenewOptInUSStates.includes(selectedCreditCard.value?.address?.stateOrProvince);
//   }
//   return response;
// });

const displayCardSelector = computed(() => userStore.currentUser && creditCards.value.length > 0);

onBeforeMount(async () => {
  try {
    logEvent("mounting", componentName);
    selectedCreditCard.value = creditCards.value[0];
    buyFlowStore.updateCreditCard(selectedCreditCard.value.id);
  } catch (ex) {
    logException(ex as Error);
  }
});

watch(selectedCreditCard, async () => {
  if (selectedCreditCard.value === "+") {
    emits("add-card");
  } else if (typeof selectedCreditCard.value !== "string" && selectedCreditCard.value) {
    buyFlowStore.updateCreditCard(selectedCreditCard.value.id);
    emits("set-display-card-details-state");
  }
});

function checkOut() {
  if (typeof selectedCreditCard.value !== "string" && selectedCreditCard.value) {
    buyFlowStore.showProcessingOrderMessage = true;
    buyFlowStore.updateCreditCard(selectedCreditCard.value.id);
    logEvent(`Credit Card Id selected: ${selectedCreditCard.value.id}`, componentName);
    emits("selected-credit-card", selectedCreditCard.value.id, false);
  }
}

// function resetToAddCardPage() {
//   selectedCreditCard.value = undefined;
//   emits("reset-flow");
// }

function tn(v: string, params?: Record<string, unknown>): string {
  return t(`${componentName}.${v}`, params);
}
</script>
<style scoped lang="css">
@import "@/styles/variables.css";
</style>
